import { useEffect, useRef, useState } from "react";

/**
 * Car-speedometer style SVG gauge (like the official Speedtest app).
 * - Log-ish scale (0..1000 Mbps) so low speeds remain readable
 * - Needle smoothed with requestAnimationFrame exponential easing
 * - Arc color changes by phase: download=green, upload=cyan
 */
const MAX_MBPS = 1000;
const START_DEG = 210; // bottom-left
const SWEEP_DEG = 240; // total sweep (through the top) to bottom-right
const MAJOR_TICKS = [0, 1, 5, 10, 50, 100, 500, 1000];

const valueToFraction = (v) =>
  Math.min(1, Math.max(0, Math.log10(1 + Math.max(0, v)) / Math.log10(1 + MAX_MBPS)));

const fractionToPoint = (cx, cy, r, f) => {
  const a = ((START_DEG - SWEEP_DEG * f) * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy - r * Math.sin(a) };
};

const describeArc = (cx, cy, r, f0, f1) => {
  const p0 = fractionToPoint(cx, cy, r, f0);
  const p1 = fractionToPoint(cx, cy, r, f1);
  const large = (f1 - f0) * SWEEP_DEG > 180 ? 1 : 0;
  return `M ${p0.x.toFixed(2)} ${p0.y.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`;
};

const PHASE_COLORS = {
  download: "#34D399",
  upload: "#22D3EE",
  ping: "rgba(34, 211, 238, 0.55)",
  connecting: "rgba(34, 211, 238, 0.4)",
};

export const SpeedometerGauge = ({ value = 0, phase = "connecting" }) => {
  const [display, setDisplay] = useState(0);
  const targetRef = useRef(0);
  const rafRef = useRef(null);
  targetRef.current = Number(value) || 0;

  useEffect(() => {
    const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const tick = () => {
      setDisplay((d) => {
        const t = targetRef.current;
        if (reduced) return t;
        const next = d + (t - d) * 0.12;
        return Math.abs(next - t) < 0.05 ? t : next;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const cx = 150;
  const cy = 150;
  const r = 112;
  const f = valueToFraction(display);
  const needleTip = fractionToPoint(cx, cy, r - 24, f);
  const color = PHASE_COLORS[phase] || PHASE_COLORS.connecting;

  // minor ticks: evenly spaced in fraction space
  const minors = [];
  for (let i = 0; i <= 48; i += 1) minors.push(i / 48);

  return (
    <svg
      viewBox="0 0 300 190"
      className="gauge-svg"
      role="img"
      aria-label={`عداد السرعة: ${display.toFixed(1)} ميجابت بالثانية`}
      data-testid="speedtest-speedometer-gauge"
    >
      {/* track */}
      <path d={describeArc(cx, cy, r, 0, 1)} fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="10" strokeLinecap="round" />
      {/* progress arc up to the needle */}
      {f > 0.005 && (
        <path d={describeArc(cx, cy, r, 0, f)} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round" style={{ transition: "stroke 180ms ease" }} />
      )}
      {/* minor ticks */}
      {minors.map((mf) => {
        const o = fractionToPoint(cx, cy, r - 14, mf);
        const i = fractionToPoint(cx, cy, r - 19, mf);
        return <line key={mf} x1={o.x} y1={o.y} x2={i.x} y2={i.y} stroke="rgba(255,255,255,0.18)" strokeWidth="1" />;
      })}
      {/* major ticks + labels */}
      {MAJOR_TICKS.map((tv) => {
        const tf = valueToFraction(tv);
        const o = fractionToPoint(cx, cy, r - 13, tf);
        const i = fractionToPoint(cx, cy, r - 22, tf);
        const l = fractionToPoint(cx, cy, r - 36, tf);
        return (
          <g key={tv}>
            <line x1={o.x} y1={o.y} x2={i.x} y2={i.y} stroke="rgba(232,238,251,0.7)" strokeWidth="2" />
            <text x={l.x} y={l.y + 4} textAnchor="middle" className="gauge-tick-label">{tv}</text>
          </g>
        );
      })}
      {/* needle */}
      <line x1={cx} y1={cy} x2={needleTip.x} y2={needleTip.y} stroke={color} strokeWidth="3.5" strokeLinecap="round" style={{ filter: "drop-shadow(0 6px 14px rgba(0,0,0,0.55))", transition: "stroke 180ms ease" }} />
      {/* hub */}
      <circle cx={cx} cy={cy} r="11" fill="rgba(255,255,255,0.10)" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
      <circle cx={cx} cy={cy} r="4" fill={color} style={{ transition: "fill 180ms ease" }} />
      {/* live readout */}
      <text x={cx} y={cy + 34} textAnchor="middle" className="gauge-readout" dir="ltr">{display.toFixed(display >= 100 ? 0 : 1)}</text>
      <text x={cx} y={cy + 52} textAnchor="middle" className="gauge-readout-unit">Mbps</text>
    </svg>
  );
};
